import React, { useState, useRef, useEffect, useMemo } from "react";
import Tree from "react-d3-tree";
import { FaUser } from "react-icons/fa";
import { Menu, MenuItem } from "@mui/material";
import { useQuery } from "react-query";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { endpoint } from "../../../utils/APIRoutes";
import { apiConnectorGet } from "../../../utils/APIConnector";
import moment from "moment/moment";
import Loader from "../../../Shared/Loader";

const Team = () => {
  const [verticaa, setVertica] = useState("vertical");
  const [pathfn, setPathFn] = useState("diagonal");
  const [showSidebar, setShowSidebar] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.8);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.2));
  const handleZoomReset = () => setZoom(0.8);

  const open = Boolean(anchorEl);
  const treeContainerRef = useRef(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data, isLoading } = useQuery(
    ["MatrixTree-downline"],
    () =>
      apiConnectorGet(endpoint.get_member_downline_tree, {
        tree_type: "MATRIX",
      }),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const matrixData = data?.data?.result || [];

  const buildTreeFrommatrixData = (matrixData) => {
    const map = {};
    let root = null;

    matrixData.forEach((item) => {
      map[item.lgn_cust_id] = {
        ...item,
        name: item.lgn_name,
        joining_date: item.tr03_reg_date,
        topup_date: item.tr03_topup_date,
        children: [],
      };
    });

    matrixData.forEach((item) => {
      const node = map[item.lgn_cust_id];
      const parent = map[item.from_cust];

      if (parent) {
        parent.children.push(node);
      } else {
        root = node;
      }
    });

    return root;
  };

  // const buildTreeFrommatrixData = (matrixData) => {
  //   const map = {};
  //   let root = null;

  //   matrixData.forEach((item) => {
  //     const formattedNode = {
  //       ...item,
  //       name: item.jnr_name,
  //       joining_date: item.tr03_reg_date,
  //       topup_date: item.tr03_topup_date,
  //       children: [],
  //     };

  //     map[item.lgn_jnr_id] = formattedNode;
  //   });

  //   matrixData.forEach((item) => {
  //     const node = map[item.lgn_jnr_id];
  //     const parent = map[item.lgn_spon_id];

  //     if (parent) {
  //       parent.children.push(node);
  //     } else {
  //       root = node;
  //     }
  //   });

  //   return root;
  // };

  const orgChart = useMemo(() => {
    return buildTreeFrommatrixData(matrixData);
  }, [matrixData]);

  useEffect(() => {
    if (treeContainerRef.current && orgChart) {
      const dimensions = treeContainerRef.current.getBoundingClientRect();
      setTranslate({
        x: dimensions.width / 2,
        y: dimensions.height / 6,
      });
    }
  }, [orgChart, verticaa]);

  const renderCustomNode = ({ nodeDatum, toggleNode }) => {
    const nodeColor = "#FFFFFF";
    const IconComponent = !nodeDatum.tr03_topup_date ? (
      <FaUser className="!text-red-600 !text-3xl" />
    ) : (
      <FaUser className="!text-green-600 !text-3xl" />
    );

    return (
      <g onClick={toggleNode} style={{ cursor: "pointer" }}>
        <circle r={30} fill="#1a1200" stroke="#D4AF37" strokeWidth={1.5} />
        <foreignObject x={-20} y={-20} width={40} height={40}>
          <div className="w-full h-full flex justify-center items-center">
            {IconComponent}
          </div>
        </foreignObject>
        <text
          x={0}
          y={50}
          textAnchor="middle"
          fontWeight="bold"
          fontSize="12"
          fill="#FFD700"
          stroke="none"
          id="basic-text"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={(e) => {
            setSelectedNode(nodeDatum);
            setAnchorEl(e.currentTarget);
          }}
        >
          {nodeDatum.name} ({nodeDatum.lgn_cust_id})
        </text>
      </g>
    );
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <div
        className="flex min-h-screen justify-center items-center relative"
        style={{
          background:
            "linear-gradient(135deg, #0a0800 0%, #1a1200 40%, #0d0900 100%)",
        }}
      >
        {/* Zoom Controls */}
        <div
          className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
          style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.4))" }}
        >
          <button
            onClick={handleZoomIn}
            className="w-10 h-10 rounded-full text-lg font-bold flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #B8860B, #FFD700)",
              color: "#0a0800",
            }}
            title="Zoom In"
          >
            +
          </button>
          <button
            onClick={handleZoomReset}
            className="w-10 h-10 rounded-full text-xs font-bold flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: "rgba(212,175,55,0.15)",
              border: "1px solid rgba(212,175,55,0.5)",
              color: "#FFD700",
            }}
            title="Reset Zoom"
          >
            ⌂
          </button>
          <button
            onClick={handleZoomOut}
            className="w-10 h-10 rounded-full text-lg font-bold flex items-center justify-center transition-all hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #B8860B, #FFD700)",
              color: "#0a0800",
            }}
            title="Zoom Out"
          >
            −
          </button>
        </div>

        {/* Zoom level badge */}
        <div
          className="fixed bottom-6 left-6 z-50 px-3 py-1 rounded-full text-xs font-bold"
          style={{
            background: "rgba(212,175,55,0.12)",
            border: "1px solid rgba(212,175,55,0.35)",
            color: "#D4AF37",
          }}
        >
          {Math.round(zoom * 100)}%
        </div>

        {/* Tree Chart */}
        <div className="flex-1 h-screen flex flex-col justify-center items-center">
          <div
            ref={treeContainerRef}
            id="treeWrapper"
            className="w-full h-full"
            style={{ maxHeight: "100vh" }}
          >
            <style>{`#treeWrapper path.rd3t-link { stroke: #ffffff !important; stroke-width: 1.5px !important; }`}</style>
            {orgChart && translate.x !== 0 && (
              <Tree
                data={orgChart}
                orientation={verticaa}
                pathFunc={pathfn}
                renderCustomNodeElement={renderCustomNode}
                zoomable={true}
                zoom={zoom}
                onUpdate={({ zoom: newZoom }) => setZoom(newZoom)}
                translate={translate}
                styles={{
                  links: { stroke: "#FFFFFF", strokeWidth: 1.5 },
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Node Details Menu */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "basic-text" }}
        PaperProps={{
          style: {
            background: "linear-gradient(145deg, #1a1200, #0f0a00)",
            border: "1px solid rgba(212,175,55,0.4)",
            boxShadow: "0 0 24px rgba(212,175,55,0.15)",
          },
        }}
      >
        <MenuItem onClick={handleClose} className="!bg-white">
          <div className="grid grid-cols-2">
            <p className="p-1 text-center text-xs border border-gray-700 font-semibold">
              Cust ID
            </p>
            <p className="p-1 text-xs text-center border border-gray-700">
              {selectedNode?.lgn_cust_id || "--"}
            </p>
            {/*<p className="p-1 text-center text-xs font-semibold border border-gray-700">
                      Joining Date
                    </p>
                    <p className="p-1 text-xs text-center border border-gray-700">
                      {selectedNode?.tr03_reg_date
                        ? moment(selectedNode?.tr03_reg_date)?.format("DD-MM-YYYY")
                        : "--"}
                    </p> */}
            <p className="p-1 text-center text-xs font-semibold border border-gray-700">
              Current Package
            </p>
            <p className="p-1 text-xs font-semibold text-center border border-gray-700">
              {selectedNode?.m03_pkg_name || "--"}
            </p>
            <p className="px-4 py-2 text-xs text-center font-semibold border border-gray-700">
              Direct Members
            </p>
            <p className="px-4 py-2 text-xs  text-center border border-gray-700">
              {selectedNode?.tr03_dir_mem || "--"}
            </p>
            <p className="px-4 py-2 text-center text-xs font-semibold border border-gray-700">
              Global Direct Members
            </p>
            <p className="px-4 py-2 text-xs  text-center border border-gray-700">
              {selectedNode?.tr03_glb_dir_mem || "--"}
            </p>
            <p className="px-4 py-2 text-center text-xs font-semibold border border-gray-700">
              Global Team Members
            </p>
            <p className="px-4 py-2 text-xs  text-center border border-gray-700">
              {Number(selectedNode?.tr03_glb_team_mem || 0)?.toFixed(2)}
            </p>
            {/* <p className="p-1 text-center text-xs font-semibold border border-gray-700">
                      Team Buss.
                    </p>
                    <p className="p-1 text-xs text-center border border-gray-700">
                      {Number(selectedNode?.tr03_team_buss || 0)?.toFixed(2)}
                    </p> */}
          </div>
        </MenuItem>
      </Menu>
    </>
  );
};

export default Team;
